import Container from '@mui/material/Container';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import TextField from "@mui/material/TextField";
import InputLabel from '@mui/material/InputLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment/InputAdornment';
import Skeleton from '@mui/material/Skeleton';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';

import {
  GridColDef,
} from '@mui/x-data-grid';

import useModal from '../../hooks/use-modal';

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { CustomDataGrid } from '../../components/common/datagrid';
import { userStyle } from '../../styles/pages/user/user';
import { filterStyle } from '../../styles/common/filter';
import { useQueryUser, useMutationUsersBulkVerification, useQueryUserRoleList } from '../../api/user';
import { UserList, UserRoleList } from '../../types/user';
import { selectAuthUser } from '../../store/auth/selector';

//
import { Theme, styled } from '@mui/material/styles';

interface ChipData {
  key: number;
  label: string;
}

const ListItem = styled('li')(({ theme }) => ({
  margin: theme.spacing(0.5),
}));

export function User(){
  const { showModal } = useModal();
  const navigate = useNavigate();
  const profile = useSelector(selectAuthUser);
  const queryClient = useQueryClient();

  const [openBulkEditModal, setOpenBulkEditModal] = useState(false)
  const [openFilterModal, setOpenFilterModal] = useState(false)
  const [chipData, setChipData] = useState<readonly ChipData[]>([]);
  const [searchResult, setSearchResult] = useState('');
  // const [doSearchClick, setDoSearchClick] = useState(false);
  const [totalItems, setTotalItems] = useState(0)
  const [paginationModel, setPaginationModel] = useState({pageSize: 10, page: 0});
  const [selectedRows, setSelectedRows] = useState<[]>([]);
  const [dataUserList, setDataUserList] = useState<UserList[]>([])
  const [dataRole, setDataRole] = useState<UserRoleList[]>([])
  const [institutionValue, setInstitution] = useState('')
  const [academicCareer, setAcademicCareer] = useState('')
  const [studyProgram, setStudyProgram] = useState('')
  const [facultyValue, setFaculty] = useState('')
  const [statusValue, setStatus] = useState('')
  const [role, setRole] = useState('')
  const [roleIdForBulk, setRoleIdForBulk] = useState('')
  const [isVerifiedForBulk, setIsVerifiedForBulk] = useState('')

  const columns: GridColDef[] = [
    {
      field: 'userId',
      headerName: 'NIM',
      minWidth: 150,
    },
    {
      field: 'fullName',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
          <>
            <Box sx={userStyle.dataGridBox}>
              <Avatar alt="user-avatar" src={params.row.avatar} />
            </Box>
            <Box>
              <Typography variant="body2">{params.row.fullName}</Typography>
            </Box>
          </>
        ),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 0.5,
      minWidth: 100,
    },
    {
      field: 'role',
      headerName: 'Role',
      flex: 0.5,
      minWidth: 100,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.5,
      minWidth: 100,
    },
    {
      field: 'actions',
      headerName: '',
      width: 250,
      align: 'center',
      renderCell: (params) => (
        <KeyboardArrowRightIcon/>
      )
    },
  ];

  const handleDelete = (chipToDelete: ChipData) => () => {
    setChipData((chips) => chips.filter((chip) => chip.key !== chipToDelete.key));
    if (chipToDelete.label === "Institution") setInstitution("")
    if (chipToDelete.label === "Academic Career") setAcademicCareer("")
    if (chipToDelete.label === "Study Program") setStudyProgram("")
    if (chipToDelete.label === "Faculty") setFaculty("")
    if (chipToDelete.label === "Status") setStatus("")
    if (chipToDelete.label === "Role") setRole("")
  };

  const applyFilter = () => {
    const data = [
      { key: 0, label: institutionValue ? "Institution" : "" },
      { key: 1, label: academicCareer ? "Academic Career" : "" },
      { key: 2, label: studyProgram ? "Study Program" : "" },
      { key: 3, label: facultyValue ? "Faculty" : "" },
      { key: 4, label: statusValue ? "Status" : "" },
      { key: 5, label: role ? "Role" : "" },
    ]

    const filteredData = data.filter((item) => item.label !== '');

    setChipData(filteredData) 
    setOpenFilterModal(false)
  };

  const resetFilter = () => {
    setChipData([])
    setInstitution("")
    setAcademicCareer("")
    setStudyProgram("")
    setFaculty("")
    setStatus("")
    setRole("")
  };

  const getUserRoleList = useQueryUserRoleList({
    options: {
      onSuccess: (res) => {
        setDataRole(res.data)
      },
      onError: () => {
        setDataRole([])
      }
    },
  });

  const getUser = useQueryUser({
    query: {
      roleId: role,
      search: searchResult,
      institution: institutionValue,
      acad: academicCareer,
      program: studyProgram,
      faculty: facultyValue,
      status: statusValue,
      take: paginationModel.pageSize,
      page: paginationModel.page
    },
    options: {
      // enabled: doSearchClick,
      onSuccess: (res) => {
        setDataUserList(res.data.users);
        setTotalItems(res.data.totalItems);
      },
      onError: () => {
        setDataUserList([]);
      }
    },
  });

  const goToUserDetail = (params) => {
    const selectedRowId = params.id

    navigate(`/user-detail/${selectedRowId}`);
  };

  const usersBulkVerification  = useMutationUsersBulkVerification({
    onSuccess: async (res) => {
      showModal({
        title: 'Success',
        message:
          'Update users bulk successfully.',
        options: {
          variant: 'success',
          onOk: () => {
            setOpenBulkEditModal(false)
            setRoleIdForBulk('')
            setIsVerifiedForBulk('')
            setSelectedRows([])
            queryClient.invalidateQueries({ queryKey: ['user'] });
          }
        },
      });
    },
    onError: (error) => {
      showModal({
        title: 'Error',
        message:
          'Update users bulk is failed.',
        options: {
          variant: 'failed',
          onOk: () => {
            setOpenBulkEditModal(false)
            setRoleIdForBulk('')
            setIsVerifiedForBulk('')
          }
        },
      });
    },
  });

  const doUsersBulkVerification = () => {
    usersBulkVerification.mutate({
      UserId: profile.userId,
      Users: selectedRows,
      All: false,
      RoleId: roleIdForBulk,
      IsVerified: JSON.parse(isVerifiedForBulk)
    })
  }

  useEffect(() => {
    if(searchResult === null || searchResult === ""){
      getUser.refetch()
    }

  }, [searchResult])


  useEffect(() => {
    if(chipData){
      getUser.refetch()
    }

  }, [chipData])

  return (
    <>
      <Container maxWidth={false} sx={userStyle.contentContainer}>
        <Grid container spacing={2}>
          <Grid xs={3}>
            <Typography variant="h3" sx={userStyle.contentContainerGridItem1Typography}>User</Typography>
          </Grid>
          <Grid xs={9}>
            <Box sx={userStyle.contentContainerGridItem2Box}>
              <TextField sx={filterStyle.contentBoxTextField}
                placeholder="Search User"
                variant="outlined"
                size="small"
                value={searchResult}
                onChange={(e) => setSearchResult(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Button variant="contained" sx={userStyle.contentContainerGridItem2BoxButton} onClick={() => setOpenFilterModal(true)}>Filter</Button>
              <Button variant="contained" color="primary" onClick={() => getUser.refetch()}>Search</Button>
            </Box>
          </Grid>
          <Grid xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <Paper
                elevation={0}
                sx={userStyle.contentContainerGridItem3Paper}
                component="ul"
              >
                {chipData.length > 0 ? (
                <>
                  {chipData.map((data) => (
                    <ListItem key={data.key}>
                      <Chip
                        label={data.label}
                        onDelete={handleDelete(data)}
                        deleteIcon={<CloseIcon fontSize="small" color="disabled" sx={userStyle.contentContainerGridItem3PaperListItemChip} />}
                      />
                    </ListItem>
                  ))}
                  <ListItem>
                    <Chip label="Reset Filter" color="error" onClick={resetFilter} />
                  </ListItem>
                </>
                ): ' '}
              </Paper>
              <Button 
                variant="contained" 
                color="success" 
                startIcon={<EditIcon />} 
                onClick={() => setOpenBulkEditModal(true)}
                disabled={selectedRows.length === undefined || selectedRows.length === 0 && true}
              >
                  Bulk Edit Users
              </Button>
            </Box>
          </Grid>
          <Grid xs={12}>
            <Paper elevation={3}>
              {getUser.isLoading || getUser.isFetching ? (
                <Skeleton variant="rounded" width="auto" height="80vh" />
              ) : (
                <CustomDataGrid
                  columnsData={columns}
                  rowsData={dataUserList}
                  rowCount={totalItems}
                  getRowId={(row) => row.userId}
                  checkboxSelection
                  keepNonExistentRowsSelected
                  hideFooterSelectedRowCount
                  rowSelectionModel={selectedRows}
                  onRowSelectionModelChange={(newRowSelectionModel) => {
                    setSelectedRows(newRowSelectionModel)
                  }}
                  onRowClick={goToUserDetail}
                  setPaginationModel={(value) => {
                    setPaginationModel(value);
                  }} 
                  paginationModel={paginationModel}
                />
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
      <Dialog
      open={openFilterModal}
      fullWidth
      maxWidth="sm"
      >
        <DialogTitle sx={userStyle.dialogDialogTitle}>Filter</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => setOpenFilterModal(false)}
          sx={userStyle.dialogIconButton}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth size="small">
                <TextField 
                  id="institution" 
                  label="Institution" 
                  variant="outlined" 
                  size="small" 
                  fullWidth 
                  value={institutionValue} 
                  onChange={(e) => setInstitution(e.target.value)}
                />
              </FormControl>
              <FormControl fullWidth size="small">
                <TextField 
                  id="academic-career" 
                  label="Academic Career" 
                  variant="outlined" 
                  size="small" 
                  fullWidth 
                  value={academicCareer} 
                  onChange={(e) => setAcademicCareer(e.target.value)}
                />
              </FormControl>
            </Stack>
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth size="small">
                <TextField 
                  id="study-program" 
                  label="Study Program" 
                  variant="outlined" 
                  size="small" 
                  fullWidth 
                  value={studyProgram} 
                  onChange={(e) => setStudyProgram(e.target.value)}
                />
              </FormControl>
              <FormControl fullWidth size="small">
                <TextField 
                  id="faculty" 
                  label="Faculty" 
                  variant="outlined" 
                  size="small"
                  fullWidth 
                  value={facultyValue} 
                  onChange={(e) => setFaculty(e.target.value)}
                />
              </FormControl>
            </Stack>
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="status-select-label">Status</InputLabel>
                <Select
                  labelId="status-select-label"
                  id="status-select"
                  value={statusValue}
                  label="Status"
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <MenuItem value="Verified">Verified</MenuItem>
                  <MenuItem value="Unverified">Unverified</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel id="role-select-label">Role</InputLabel>
                <Select
                  labelId="role-select-label"
                  id="role-select"
                  value={role}
                  label="Role"
                  onChange={(e) => setRole(e.target.value)}
                >
                  {dataRole.map(item => (
                    <MenuItem key={item.roleId} value={item.roleId}>{item.roleName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="success" onClick={applyFilter}>Apply Filter</Button>
        </DialogActions>
      </Dialog>
      <Dialog
      open={openBulkEditModal}
      fullWidth
      maxWidth="sm"
      >
        <DialogTitle sx={userStyle.dialogDialogTitle}>Edit Users in Bulk</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => setOpenBulkEditModal(false)}
          sx={userStyle.dialogIconButton}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Stack spacing={2}>
            <Stack direction="column" spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="status-select-label">Status</InputLabel>
                <Select
                  labelId="status-select-label"
                  id="status-select"
                  value={isVerifiedForBulk}
                  label="Status"
                  onChange={(e) => setIsVerifiedForBulk(e.target.value)}
                >
                  <MenuItem value="true">Verified</MenuItem>
                  <MenuItem value="false">Unverified</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel id="role-select-label">Role</InputLabel>
                <Select
                  labelId="role-select-label"
                  id="role-select"
                  value={roleIdForBulk}
                  label="Role"
                  onChange={(e) => setRoleIdForBulk(e.target.value)}
                >
                  {dataRole.map(item => 
                    isVerifiedForBulk == 'true' && item.roleName == 'Unassigned' ? null :
                    (
                      <MenuItem key={item.roleId} value={item.roleId}>{item.roleName}</MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="success" onClick={doUsersBulkVerification}>Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}